import type { QueryKey } from '@tanstack/react-query';

import useQuery from '../../../ReactQuery/useQuery';
import { get } from '../../../redux/api';
import type { DependencyCountResponse } from '../../../../shared/dependenciesV2';
import type { DependencySource } from '../../../../shared/vendoredDependencies';

type DependencyCountParams = {
  sources?: DependencySource[];
};

function getReleaseGroupDependenciesCount(
  groupId: number,
  releaseId: number,
  params?: DependencyCountParams,
) {
  return get<DependencyCountResponse>(
    `/api/v2/release-groups/${groupId}/releases/${releaseId}/dependencies/count`,
    false,
    params,
  );
}

export const getReleaseGroupDependenciesCountQueryKey = (
  groupId: number,
  releaseId: number,
  params?: DependencyCountParams,
): QueryKey => [
  'release-groups',
  groupId,
  'releases',
  releaseId,
  'dependencies',
  'count',
  params,
];

/**
 * Hook to fetch the count of dependencies for a given release group release.
 *
 * @param {number} groupId - The ID of the release group.
 * @param {number} releaseId - The ID of the release group release.
 * @param {DependencyCountParams} params - Optional params to filter the count (e.g. by source).
 * @param {boolean} [shouldFetch] - Whether to fetch the count.
 */
export function useReleaseGroupDependenciesCount(
  groupId: number,
  releaseId: number,
  params?: DependencyCountParams,
  shouldFetch = true,
) {
  return useQuery<DependencyCountResponse>({
    queryFn: () => getReleaseGroupDependenciesCount(groupId, releaseId, params),
    queryKey: getReleaseGroupDependenciesCountQueryKey(groupId, releaseId, params),
    enabled: shouldFetch && groupId > 0 && releaseId > 0,
  });
}
